import type { Catalog, InterpolationValues, Message, PluralMessage } from './types.js';

/**
 * Placeholders use `{name}`. Values are inserted as text; the translator never produces HTML,
 * so a translated string can never introduce markup into a page (docs/SECURITY.md).
 */
const PLACEHOLDER = /\{(\w+)\}/g;

function isPlural(message: Message): message is PluralMessage {
  return typeof message !== 'string';
}

export interface TranslatorOptions {
  readonly locale: string;
  readonly catalog: Catalog;
  /** Consulted when the active catalogue has no entry for a key. */
  readonly fallbackCatalog?: Catalog;
  /** Called for a missing key. Production wires this to a metric; tests assert on it. */
  readonly onMissing?: (key: string, locale: string) => void;
}

export interface Translator {
  readonly locale: string;
  /** Translates `key`, interpolating `values`. `count` additionally selects the plural form. */
  t(key: string, values?: InterpolationValues): string;
  has(key: string): boolean;
  formatNumber(value: number, options?: Intl.NumberFormatOptions): string;
  formatDate(value: Date | number, options?: Intl.DateTimeFormatOptions): string;
  /** "3 days ago" / "over 3 dagen" — used throughout Today and the Decision Queue. */
  formatRelativeTime(value: Date | number, now?: Date | number): string;
  formatList(values: readonly string[], type?: Intl.ListFormatType): string;
}

const RELATIVE_UNITS: readonly (readonly [Intl.RelativeTimeFormatUnit, number])[] = [
  ['year', 365 * 24 * 60 * 60 * 1000],
  ['month', 30 * 24 * 60 * 60 * 1000],
  ['week', 7 * 24 * 60 * 60 * 1000],
  ['day', 24 * 60 * 60 * 1000],
  ['hour', 60 * 60 * 1000],
  ['minute', 60 * 1000],
  ['second', 1000],
];

export function createTranslator(options: TranslatorOptions): Translator {
  const { locale, catalog, fallbackCatalog, onMissing } = options;
  const pluralRules = new Intl.PluralRules(locale);

  function resolve(key: string): Message | undefined {
    return catalog[key] ?? fallbackCatalog?.[key];
  }

  function selectForm(message: Message, values: InterpolationValues | undefined): string {
    if (!isPlural(message)) return message;

    const count = values?.['count'];
    if (typeof count !== 'number') return message.other;

    // `zero` is an explicit authoring choice ("no messages"), not a CLDR category, so it is
    // checked before the rule-based selection.
    if (count === 0 && message.zero !== undefined) return message.zero;

    const category = pluralRules.select(count);
    return message[category] ?? message.other;
  }

  function interpolate(template: string, values: InterpolationValues | undefined): string {
    if (values === undefined) return template;

    return template.replace(PLACEHOLDER, (match, name: string) => {
      const value = values[name];
      if (value === undefined) return match;
      if (value instanceof Date) return new Intl.DateTimeFormat(locale).format(value);
      if (typeof value === 'number') return new Intl.NumberFormat(locale).format(value);
      return value;
    });
  }

  return {
    locale,

    t(key, values) {
      const message = resolve(key);
      if (message === undefined) {
        onMissing?.(key, locale);
        // Returning the key keeps the UI usable and makes the gap obvious in screenshots.
        return key;
      }
      return interpolate(selectForm(message, values), values);
    },

    has(key) {
      return resolve(key) !== undefined;
    },

    formatNumber(value, formatOptions) {
      return new Intl.NumberFormat(locale, formatOptions).format(value);
    },

    formatDate(value, formatOptions) {
      return new Intl.DateTimeFormat(locale, formatOptions).format(value);
    },

    formatRelativeTime(value, now = Date.now()) {
      const formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
      const delta =
        (value instanceof Date ? value.getTime() : value) -
        (now instanceof Date ? now.getTime() : now);

      for (const [unit, ms] of RELATIVE_UNITS) {
        if (Math.abs(delta) >= ms || unit === 'second') {
          return formatter.format(Math.round(delta / ms), unit);
        }
      }
      return formatter.format(0, 'second');
    },

    formatList(values, type = 'conjunction') {
      return new Intl.ListFormat(locale, { style: 'long', type }).format(values);
    },
  };
}
